import {
  ChangeEvent,
  useCallback,
  useMemo,
  MutableRefObject,
  ReactNode,
  MouseEventHandler
} from 'react';

import classNames from 'classnames';
import { RegisterOptions, UseFormRegister, FieldErrors } from 'react-hook-form';

import { Error } from 'components/ui/forms';
import { Icon, IconProps, Information } from 'components/ui/general';
import { TextSelectors } from 'consts/cypress';
import { Fonts } from 'types/icon';

import styles from './Text.module.scss';

type IconPropsExtended = IconProps & {
  onClick?: MouseEventHandler;
};

export interface TextProps {
  name: string;
  type?: string;
  placeholder?: string;
  ariaLabel?: string;
  label?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  color?: 'light'; // color names $all-colors
  disabled?: boolean;
  register: UseFormRegister<any>;
  validation?: RegisterOptions;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
  error?: FieldErrors;
  iconRight?: IconPropsExtended;
  iconLeft?: IconPropsExtended;
  rounded?: boolean;
  defaultValue?: string;
  information?: ReactNode;
  success?: boolean;
  readOnly?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  textRight?: ReactNode;
  textLeft?: ReactNode;
}

export const Text = ({
  name,
  type = 'text',
  placeholder,
  ariaLabel,
  label,
  size = 'md',
  color = 'light',
  className,
  disabled,
  register,
  validation = {},
  onChange,
  onBlur,
  fullWidth,
  error,
  iconRight,
  iconLeft,
  rounded,
  defaultValue,
  information,
  success,
  readOnly,
  inputRef,
  textRight,
  textLeft
}: TextProps) => {
  const Root = useMemo(() => (label ? 'label' : 'div'), [label]);

  const getIconFont = useCallback(
    ({ font }: { font?: Fonts }) => {
      if (font) {
        return font;
      }

      switch (size) {
        case 'sm':
        case 'md':
        case 'lg':
          return 'hedemora';

        default:
          return 'hedemora';
      }
    },
    [size]
  );

  const renderIcon = useCallback(
    (icon: IconPropsExtended) => {
      const { name: iconName, onClick } = icon;
      const IconButton = onClick ? 'button' : 'div';
      const disabledOrReadOnly = disabled || readOnly;

      return (
        <IconButton
          type={onClick ? 'button' : undefined}
          onClick={onClick && !disabledOrReadOnly ? onClick : undefined}
          className={onClick ? styles.iconButton : undefined}
        >
          <Icon name={iconName} font={getIconFont(icon)} />
        </IconButton>
      );
    },
    [disabled, getIconFont, readOnly]
  );

  const getValidation = useMemo(() => {
    return !disabled && !readOnly ? validation : {};
  }, [disabled, readOnly, validation]);

  const registerHolder = useMemo(
    () => register(name, getValidation),
    [name, register, getValidation]
  );

  return (
    <>
      <Root
        className={classNames(styles.root, className, {
          [styles.disabled]: disabled,
          [styles[`${color}Color`]]: color,
          [styles[`${size}Size`]]: size,
          [styles.fullWidth]: fullWidth,
          [styles.hasIconRight]: iconRight,
          [styles.hasIconLeft]: iconLeft,
          [styles.rounded]: rounded,
          [styles.error]: !!error,
          [styles.success]: success,
          [styles.readOnly]: readOnly,
          [styles.hasTextRight]: textRight,
          [styles.hasTextLeft]: textLeft
        })}
        data-cy={TextSelectors.Root}
      >
        {!!label && (
          <div className={styles.label}>
            {label}
            {getValidation.required ? ' *' : ''}
          </div>
        )}
        <div className={styles.inputHolder}>
          <input
            type={type}
            disabled={disabled}
            autoComplete="off"
            placeholder={placeholder}
            aria-label={ariaLabel || placeholder}
            className={styles.input}
            defaultValue={defaultValue}
            readOnly={readOnly}
            data-cy={TextSelectors.Input}
            {...registerHolder}
            ref={(event) => {
              if (inputRef) inputRef.current = event;
              registerHolder.ref(event);
            }}
            onChange={(event) => {
              registerHolder.onChange(event);
              onChange?.(event);
            }}
            onBlur={(event) => {
              registerHolder.onBlur(event);
              onBlur?.(event);
            }}
          />
          {!!iconLeft && (
            <div className={styles.iconLeft}>{renderIcon(iconLeft)}</div>
          )}
          {!!textLeft && <div className={styles.textLeft}>{textLeft}</div>}
          {!!iconRight && (
            <div className={styles.iconRight}>{renderIcon(iconRight)}</div>
          )}
          {!!textRight && <div className={styles.textRight}>{textRight}</div>}
        </div>
      </Root>
      {!!information && (
        <Information className={styles.informationText}>
          {information}
        </Information>
      )}
      <Error error={error} />
    </>
  );
};
